import { useState } from "react";
import { SidebarInset } from "@/components/ui/sidebar";
import { PageContainer } from "@/components/ui/PageContainer";
import AssetsPageHeader from "@/components/assets/AssetsPageHeader";
import { useAssetsUnifiedData } from "@/hooks/useAssetsUnifiedData";
import type { UnifiedAsset } from "@/hooks/useAssetsUnified";
import { AssetsToolbar } from "@/components/assets/AssetsToolbar";
import { AssetsTableContainer } from "@/components/assets/AssetsTableContainer";
import AssetsHeader from "@/components/assets/AssetsHeader";
import AssetsSummaryHeader from "@/components/assets/AssetsSummaryHeader";
import AssetsDialogs from "@/components/assets/AssetsDialogs";
import { MobileViewportWarning, useIsMobileViewport } from "@/components/ui/MobileViewportWarning";
import { useAddPositions } from "@/hooks/investments/useAddPositions";
import { useAssetsDeepLink } from "@/hooks/useAssetsDeepLink";
import { useAssetsBulkExport } from "@/hooks/useAssetsBulkExport";
import { useInvestmentHoldings } from "@/providers/manual/hooks/useInvestmentHoldings";
import { useManualAssets } from "@/providers/manual/hooks/useManualAssets";
import { createBuildPortfolioHandlers } from "@/utils/buildPortfolioHandlers";
import { useAssetDeletion } from "@/components/assets/deletion/useAssetDeletion";
import { useAssetsSelection } from "@/components/assets/hooks/useAssetsSelection";
import { useAssetDetailDrawer } from "@/components/assets/hooks/useAssetDetailDrawer";
import { useAuthStore } from "@/stores/authStore";
import { useProfile } from "@/hooks/useProfile";
// local Input not used in page after extraction

const Assets = () => {
  // Deprecated ManualAssetDialog state removed; use InstrumentDetailDrawer for create/edit
  const { user } = useAuthStore();
  const { profile } = useProfile(user);
  const isMobileViewport = useIsMobileViewport(640);
  const { selectedAssets, setSelectedAssets, selectAllFiltered } = useAssetsSelection();
  const [showAssetTypeFilterDialog, setShowAssetTypeFilterDialog] = useState(false);
  const [collectedPositions, setCollectedPositions] = useState<any[]>([]);
  const { openAddPositions } = useAddPositions();
  const {
    detailAsset,
    setDetailAsset,
    showDetailDrawer,
    setShowDetailDrawer,
    detailMode,
    setDetailMode,
    handleAddAsset,
    handleEditAsset,
    handleViewDetails,
    handleCloseDetailDrawer,
  } = useAssetDetailDrawer();
  const [showAiImportDialog, setShowAiImportDialog] = useState(false);
  // Progress UI handled inside useAssetDeletion
  
  const {
    assets, // full unified assets list
    paginatedAssets,
    filteredAssets,
    stats,
    filters,
    sortConfig,
    filterOptions,
    isLoading,
    updateFilters,
    updateSort,
    resetFilters,
    totalFilteredCount,
    refetch,
    // Pagination integration
    pagination,
    totalPages,
    goToPage,
    changePageSize,
  } = useAssetsUnifiedData();

  // Investment holdings hook for build portfolio functionality
  const {
    holdings: investmentHoldings,
    refreshHoldings
  } = useInvestmentHoldings();

  // Safe manual asset deletion (checks for portfolio allocations first)
  const { deleteManualAsset, checkPortfolioAllocationsBulk } = useManualAssets();

  // Deletion flow extracted into hook (after data hooks so dependencies are defined)
  const {
    showDeleteConfirm,
    setShowDeleteConfirm,
    showMixedDeleteConfirm,
    setShowMixedDeleteConfirm,
    showCascadeConfirm,
    setShowCascadeConfirm,
    cascadeBlocked,
    cascadeDeletable,
    cascadeShowAll,
    setCascadeShowAll,
    mixedDeleteInfo,
    handleBulkDelete,
    confirmDelete,
    confirmDeleteManualOnly,
    confirmCascadeDelete,
    handleClearSelection,
  } = useAssetDeletion({
    filteredAssets,
    selectedAssets,
    setSelectedAssets,
    checkPortfolioAllocationsBulk,
    deleteManualAsset: async (id: string, cascade?: boolean) => { await deleteManualAsset(id, cascade); return; },
    refetch,
  });

  // Handle opening dialog for adding/editing asset handled by useAssetDetailDrawer

  // Handle dialog close
  // Removed legacy dialog close handler

  // Handle opening/closing detail drawer handled by useAssetDetailDrawer

  // Deep-link support: open drawer when routing provides asset/account id
  useAssetsDeepLink(assets, setDetailMode, setDetailAsset, setShowDetailDrawer);

  // Bulk action handlers
  const handleAddToPortfolio = () => {
    // Use the existing build-portfolio flow (same as Investments)
    // This opens the Asset Type Filter dialog and then Add Positions dialog
    handleBuildPortfolio();
  };

  const handleBulkExport = useAssetsBulkExport(filteredAssets, selectedAssets);



  // handleBulkDelete provided by useAssetDeletion

  // confirmDelete provided by useAssetDeletion

  // confirmDeleteManualOnly provided by useAssetDeletion

  // confirmCascadeDelete provided by useAssetDeletion

  // handleClearSelection provided by useAssetDeletion

  // Delete enablement handled inside table and dialogs; removed local memo

  // Build portfolio handlers using the same flow as Investments page
  const { handleBuildPortfolio } = createBuildPortfolioHandlers({
    investmentHoldings,
    refreshHoldings,
    setCollectedPositions,
    setShowAssetTypeFilterDialog
  });

  // Row delete handler: manual-only; uses existing deletion flow
  const handleRowDelete = (asset: UnifiedAsset) => {
    if ((asset as any)?.source_type === 'manual') {
      setSelectedAssets([asset.id]);
      setShowDeleteConfirm(true);
    }
  };

  // Pagination input state moved to AssetsTableContainer

  return (
    <div className="flex-1">
      <SidebarInset>
        <AssetsPageHeader />

        <PageContainer maxWidthClassName="max-w-[1320px]">
          {/* Title, subtitle, and actions area */}
          <div className="flex flex-col mb-2 mr-auto w-full max-w-[960px]">
            <AssetsHeader
              assets={filteredAssets || []}
              onAddAsset={handleAddAsset}
              onBuildPortfolio={handleBuildPortfolio}
              onAiImport={() => setShowAiImportDialog(true)}
            />
            {/* Header and toolbar hidden when there are no assets */}
            {stats.totalCount > 0 && (
              <>
                <div className="mt-6 mb-6">
                  <AssetsSummaryHeader 
                    stats={stats as any} 
                    defaultCurrency={(profile?.default_currency || 'CAD') as any}
                  />
                </div>
                <AssetsToolbar
                  filters={filters}
                  updateFilters={updateFilters}
                  resetFilters={resetFilters}
                  filterOptions={filterOptions}
                  totalFilteredCount={totalFilteredCount}
                  totalCount={stats.totalCount}
                />
              </>
            )}
            {/* Page-level mobile notice aligned with content */}
            <MobileViewportWarning
              message="For the best experience viewing your assets table, please use a tablet or desktop device."
              show={isMobileViewport}
            />
          </div>

          {/* Table area extracted */}
          {!isMobileViewport && (
          <AssetsTableContainer
            isLoading={isLoading}
            totalAssetCount={stats.totalCount}
            filteredAssetsCount={filteredAssets?.length || 0}
            totalFilteredCount={totalFilteredCount}
            allAssets={assets}
            paginatedAssets={paginatedAssets}
            sortConfig={sortConfig}
            onSort={updateSort}
            selectedAssets={selectedAssets}
            onSelectionChange={setSelectedAssets}
            onEdit={handleEditAsset}
            onViewDetails={handleViewDetails}
            onAssetAdded={refetch}
            onRowDelete={handleRowDelete}
            onAddAsset={handleAddAsset}
            onClearFilters={resetFilters}
            onClearSelection={handleClearSelection}
            onAddToPortfolio={handleAddToPortfolio}
            onBulkExport={handleBulkExport}
            onBulkDelete={handleBulkDelete}
            onSelectAllFiltered={() => selectAllFiltered(filteredAssets || [])}
            pagination={pagination}
            totalPages={totalPages}
            goToPage={goToPage}
            changePageSize={changePageSize}
            onAiImport={() => setShowAiImportDialog(true)}
          />
          )}
        </PageContainer>
      </SidebarInset>
      
      {/* ManualAssetDialog removed: edit is handled inline by InstrumentDetailDrawer */}

      <AssetsDialogs
        showAssetTypeFilterDialog={showAssetTypeFilterDialog}
        setShowAssetTypeFilterDialog={setShowAssetTypeFilterDialog}
        collectedPositions={collectedPositions}
        onFilterContinue={(positions) => {
          setShowAssetTypeFilterDialog(false);
          openAddPositions({
            positions,
            navigateOnZeroAdded: true,
            onClose: () => {
              setCollectedPositions([]);
              refetch();
              refreshHoldings();
            }
          });
        }}
        detailAsset={detailAsset}
        showDetailDrawer={showDetailDrawer}
        onCloseDetailDrawer={handleCloseDetailDrawer}
        detailMode={detailMode}
        onInstrumentCreated={() => {
          refetch();
        }}
        selectedAssetsCount={selectedAssets.length}
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        confirmDelete={confirmDelete}
        showMixedDeleteConfirm={showMixedDeleteConfirm}
        setShowMixedDeleteConfirm={setShowMixedDeleteConfirm}
        mixedDeleteInfo={mixedDeleteInfo}
        confirmDeleteManualOnly={confirmDeleteManualOnly}
        showCascadeConfirm={showCascadeConfirm}
        setShowCascadeConfirm={setShowCascadeConfirm}
        cascadeBlocked={cascadeBlocked}
        cascadeDeletable={cascadeDeletable}
        cascadeShowAll={cascadeShowAll}
        setCascadeShowAll={setCascadeShowAll}
        confirmCascadeDelete={confirmCascadeDelete}
        showAiImportDialog={showAiImportDialog}
        setShowAiImportDialog={setShowAiImportDialog}
        onAiImported={() => {
          refetch();
          refreshHoldings();
        }}
      />
    </div>
  );
};

export default Assets;
